/**
 * Resource type with optional AmountUnit and ResourcesRanges properties.
 * Compatible with TPlanResource from @settings/data-access.
 */
export interface PostPaidResource {
  AmountUnit?: number | null;
  ResourcesRanges?: unknown[] | null;
}

/**
 * Determines if a resource has unitary consumption (AmountUnit defined and >= 0).
 *
 * @param resource - The resource to check
 * @returns True if the resource has unitary consumption
 */
export function hasUnitaryConsumption(resource: PostPaidResource | null | undefined): boolean {
  return resource?.AmountUnit != null && resource.AmountUnit >= 0;
}

/**
 * Determines if a resource has range consumption (ResourcesRanges with items).
 *
 * @param resource - The resource to check
 * @returns True if the resource has range consumption
 */
export function hasRangeConsumption(resource: PostPaidResource | null | undefined): boolean {
  return (resource?.ResourcesRanges?.length ?? 0) > 0;
}

/**
 * Determines if a collection of resources contains any post-paid resource.
 * A resource is considered post-paid if it has either:
 * - Unitary consumption (AmountUnit defined and >= 0)
 * - Range consumption (ResourcesRanges with items)
 *
 * @param resources - Array of resources to check
 * @returns True if any resource is post-paid, false otherwise
 *
 * @example
 * isPostPaid([{ AmountUnit: 10 }]) // returns true
 * isPostPaid([{ ResourcesRanges: [{ min: 0, max: 100 }] }]) // returns true
 * isPostPaid([{ AmountUnit: null }]) // returns false
 * isPostPaid([]) // returns false
 * isPostPaid(null) // returns false
 */
export function isPostPaid(resources: PostPaidResource[] | null | undefined): boolean {
  if (!resources?.length) {
    return false;
  }

  const unitaryConsumption = resources.some(hasUnitaryConsumption);
  const rangeConsumption = resources.some(hasRangeConsumption);

  return unitaryConsumption || rangeConsumption;
}
